import { type Ref } from "react"
import { cn } from "cn"

import IconChevronDown from "@/components/icons/chevron-down"
import IconChevronLeft from "@/components/icons/chevron-left"
import IconChevronRight from "@/components/icons/chevron-right"
import IconChevronUp from "@/components/icons/chevron-up"
import IconCircleCheck from "@/components/icons/circle-check"
import IconCirclePlus from "@/components/icons/circle-plus"
import IconCopy from "@/components/icons/copy"
import IconDownload from "@/components/icons/download"
import IconEye from "@/components/icons/eye"
import IconFeedback from "@/components/icons/feedback"
import IconHeart from "@/components/icons/heart"
import IconPause from "@/components/icons/pause"
import IconPlay from "@/components/icons/play"
import IconPlayFill from "@/components/icons/play-fill"
import IconSkipBackward from "@/components/icons/skip-backward"
import IconSkipForward from "@/components/icons/skip-forward"
import IconTrash from "@/components/icons/trash"
import IconUpload from "@/components/icons/upload"
import {
  resolveIconSpec,
  type IconComponent,
  type IconSpec,
} from "../icons/icon"
import { Button, ButtonProps } from "./button"
import {
  Tooltip,
  TooltipContent,
  TooltipContentProps,
  TooltipTrigger,
} from "./tooltip"

export interface IconButtonProps extends Omit<
  ButtonProps,
  "iconBefore" | "iconAfter"
> {
  icon: IconSpec
  label: string
  tooltip?: TooltipContentProps | boolean
}

// Optical adjustments for icons that look too small, large or off-centre in a circle
const iconsWithExtraClasses = new Map<IconComponent, string>([
  [IconChevronLeft, cn("ml-[-.125em] stroke-[.125em] text-[1.25em]")],
  [IconChevronRight, cn("mr-[-.125em] stroke-[.125em] text-[1.25em]")],
  [IconChevronUp, cn("-mt-px stroke-[.125em] text-[1.25em]")],
  [IconChevronDown, cn("-mb-px stroke-[.125em] text-[1.25em]")],
  [IconCopy, cn("text-[1.375em]")],
  [IconFeedback, cn("text-[1.7em]")],
  [IconHeart, cn("mt-[.0625em]")],
  [IconTrash, cn("text-[1.25em]")],
  [IconPlay, cn("ml-[.125em] text-[1.375em]")],
  // A triangle's optical centre sits right of its bounding box's.
  [IconPlayFill, cn("ml-[.125em]")],
  [IconPause, cn("text-[1.375em]")],
  [IconSkipBackward, cn("text-[1.25em]")],
  [IconSkipForward, cn("text-[1.25em]")],
  [IconDownload, cn("text-[1.375em]")],
  [IconUpload, cn("text-[1.375em]")],
  [IconCirclePlus, cn("text-[1.7em]")],
  [IconCircleCheck, cn("text-[1.7em]")],
  [IconEye, cn("text-[1.7em]")],
])

function IconButtonIcon({ spec }: { spec: IconSpec }) {
  const { Icon, props } = resolveIconSpec(spec)
  return (
    <Icon
      {...props}
      className={cn(
        "text-[1.5em]",
        iconsWithExtraClasses.get(Icon),
        props.className
      )}
    />
  )
}

function IconButton({
  ref,
  icon,
  label,
  disabled,
  tooltip,
  size = "sm",
  ...props
}: IconButtonProps & { ref?: Ref<HTMLButtonElement | HTMLAnchorElement> }) {
  const button = (
    <Button
      ref={ref}
      circle
      size={size}
      disabled={disabled}
      aria-label={label}
      data-slot="icon-button"
      {...props}
    >
      <IconButtonIcon spec={icon} />
    </Button>
  )
  if (tooltip && !disabled) {
    return (
      <Tooltip>
        <TooltipTrigger render={button} />
        <TooltipContent
          {...(typeof tooltip === "object"
            ? { ...tooltip, children: <span>{tooltip.children || label}</span> }
            : { children: <span>{label}</span> })}
        />
      </Tooltip>
    )
  } else {
    return button
  }
}

IconButton.displayName = "IconButton"

export { IconButton }
